import { Mic, MicOff, Paperclip, Send, Square } from "lucide-react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useVoiceInput } from "./useVoiceInput"
import { MessageFileChip } from "./_MessageFileChip"
import { EmotePicker } from "./EmotePicker"
import { PromptArchivePicker } from "./PromptArchivePicker"

const MAX_FILES = 5
const MAX_FILE_BYTES = 100 * 1024 * 1024
const MAX_TOTAL_BYTES = 200 * 1024 * 1024
const MAX_FILE_MIB = MAX_FILE_BYTES / (1024 * 1024)
const MAX_TOTAL_MIB = MAX_TOTAL_BYTES / (1024 * 1024)

interface Props {
  onSend: (text: string, files: File[]) => void
  onCancel?: () => void
  busy: boolean
  disabled?: boolean
  /** Optionaler Slot für Quick-Buttons rechts neben dem Hint.
   * Bekommt eine `insert(prefix)`-Funktion, die Text ins Eingabefeld
   * setzt und den Fokus dorthin legt. */
  quickActions?: (insert: (prefix: string) => void) => React.ReactNode
}

export function MessageInput({ onSend, onCancel, busy, disabled, quickActions }: Props) {
  const { t } = useTranslation("chat")
  const [text, setText] = useState("")
  const voice = useVoiceInput((transcript) => {
    setText((prev) => (prev ? prev + " " + transcript : transcript))
  })
  const [files, setFiles] = useState<File[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const textRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  function submit() {
    const trimmed = text.trim()
    if ((!trimmed && files.length === 0) || busy) return
    onSend(trimmed, files)
    setText("")
    setFiles([])
    setUploadError(null)
    if (textRef.current) textRef.current.style.height = "auto"
  }

  function handleKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit() }
  }

  function insertEmote(shortcode: string) {
    setText((prev) => (prev && !prev.endsWith(" ") ? prev + " " : prev) + shortcode + " ")
    textRef.current?.focus()
  }

  function insertPrompt(promptText: string) {
    setText(promptText)
    textRef.current?.focus()
  }

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setText(e.target.value)
    const el = e.target
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 200) + "px"
  }

  function addFiles(incoming: FileList | null) {
    if (!incoming) return
    const next = [...files]
    let total = next.reduce((sum, file) => sum + file.size, 0)
    let error: string | null = null
    for (const file of Array.from(incoming)) {
      if (next.length >= MAX_FILES) {
        error ??= t("upload.too_many", { maxFiles: MAX_FILES })
        break
      }
      if (file.size > MAX_FILE_BYTES) {
        error ??= t("upload.file_too_large", { name: file.name, maxMiB: MAX_FILE_MIB })
        continue
      }
      if (total + file.size > MAX_TOTAL_BYTES) {
        error ??= t("upload.total_too_large", { maxMiB: MAX_TOTAL_MIB })
        continue
      }
      next.push(file)
      total += file.size
    }
    setFiles(next)
    setUploadError(error)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files)
  }

  return (
    <div className="border-t border-[#2a364b] bg-[#111827] p-[10px]"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {files.map((f, i) => (
            <MessageFileChip
              key={i}
              file={f}
              onRemove={() => { setFiles(files.filter((_, j) => j !== i)); setUploadError(null) }}
            />
          ))}
        </div>
      )}
      {uploadError && <p role="alert" className="mb-2 text-xs text-rose-400">{uploadError}</p>}
      <div className={`flex items-end gap-2 rounded-[4px] border bg-[#080d15] px-3 py-2 transition-colors
        ${dragOver ? "border-[#69d7ff]/60 bg-[#0d1420]" : "border-[#2a364b] focus-within:border-[#46617f]"}`}>
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => fileRef.current?.click()}
          title={t("upload.button_title")}
          className="flex-shrink-0 p-1.5 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <Paperclip size={15} />
        </button>
        <input ref={fileRef} type="file" multiple className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = "" }} />
        <button
          type="button"
          disabled={disabled || busy || voice.state === "transcribing"}
          onClick={voice.toggle}
          title={voice.state === "recording" ? t("voice.click_to_stop") : t("voice.click_to_speak")}
          className={`flex-shrink-0 p-1.5 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed
            ${voice.state === "recording"
              ? "text-rose-400 bg-rose-500/20 animate-pulse"
              : voice.state === "transcribing"
              ? "text-amber-400 bg-amber-500/10"
              : "text-zinc-500 hover:text-zinc-300 hover:bg-white/5"}`}
        >
          {voice.state === "recording" ? <MicOff size={15} /> : <Mic size={15} />}
        </button>
        <EmotePicker onPick={insertEmote} disabled={disabled || busy} />
        <PromptArchivePicker onPick={insertPrompt} disabled={disabled || busy} />
        <textarea
          ref={textRef}
          value={text}
          onChange={handleInput}
          onKeyDown={handleKey}
          placeholder={disabled ? t("input.placeholder_disabled") : t("input.placeholder")}
          disabled={disabled}
          rows={1}
          className="flex-1 bg-transparent text-sm text-zinc-200 placeholder:text-zinc-600 resize-none focus:outline-none py-1.5 disabled:opacity-50"
        />
        {busy && onCancel ? (
          <button onClick={onCancel} type="button" title={t("input.stop_title")}
            className="flex-shrink-0 p-2 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 transition-all">
            <Square size={14} fill="currentColor" />
          </button>
        ) : (
          <button onClick={submit} disabled={busy || (!text.trim() && files.length === 0) || disabled}
            className="flex-shrink-0 rounded-[4px] bg-gradient-to-br from-[#1fb6ff] to-[#8b5cf6] p-2 text-white transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-30">
            <Send size={15} />
          </button>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 mt-1.5 px-1">
        <p className="text-[10px] text-zinc-600">{t("input.hint")}</p>
        {quickActions && (
          <div className="flex items-center gap-1">
            {quickActions((prefix) => {
              setText(prefix.endsWith(" ") ? prefix : prefix + " ")
              textRef.current?.focus()
            })}
          </div>
        )}
      </div>
    </div>
  )
}

